/**
 * Deterministic label colours for chips/badges (currencies, tags, etc.).
 *
 * Use this everywhere a free-form label needs a colour so the whole app shares
 * one palette and one assignment rule. Two entry points:
 *  - `labelColor(key)` — stable colour for a standalone label (hash-based).
 *  - `distinctLabelColors(keys)` — collision-free assignment across a known set
 *    so sibling labels never share a colour (until the palette is exhausted).
 *
 * Palette entries are written as full Tailwind class names so the JIT compiler
 * keeps them; hues are chosen to be visually well separated (no near-duplicates).
 */

export type LabelColor = { bg: string; text: string };

export const LABEL_PALETTE: LabelColor[] = [
  { bg: 'bg-red-100', text: 'text-red-800' },
  { bg: 'bg-orange-100', text: 'text-orange-800' },
  { bg: 'bg-amber-100', text: 'text-amber-800' },
  { bg: 'bg-green-100', text: 'text-green-800' },
  { bg: 'bg-teal-100', text: 'text-teal-800' },
  { bg: 'bg-cyan-100', text: 'text-cyan-800' },
  { bg: 'bg-blue-100', text: 'text-blue-800' },
  { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  { bg: 'bg-violet-100', text: 'text-violet-800' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800' },
  { bg: 'bg-pink-100', text: 'text-pink-800' },
  { bg: 'bg-slate-200', text: 'text-slate-800' },
];

/** FNV-1a — small, fast, well-distributed for short label keys. */
function hashKey(key: string): number {
  let hash = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** Stable colour for a single label. Same key → same colour, app-wide. */
export function labelColor(key: string): LabelColor {
  return LABEL_PALETTE[hashKey(key) % LABEL_PALETTE.length]!;
}

/**
 * Assign every key a distinct palette colour. Deterministic (keys are sorted and
 * each starts from its hashed slot, then linear-probes to the next free one), so
 * the same set always yields the same mapping and no two keys repeat a colour
 * until the palette is exhausted.
 */
export function distinctLabelColors(keys: Iterable<string>): Map<string, LabelColor> {
  const unique = [...new Set(keys)].sort();
  const used = new Set<number>();
  const result = new Map<string, LabelColor>();
  const size = LABEL_PALETTE.length;

  for (const key of unique) {
    let index = hashKey(key) % size;
    for (let probe = 0; probe < size && used.has(index); probe += 1) {
      index = (index + 1) % size;
    }
    used.add(index);
    result.set(key, LABEL_PALETTE[index]!);
  }

  return result;
}
